import React, { useState } from 'react';
import { gnnApi, NodeDetails, PredictionResponse } from '../services/api';
import { PageTransition } from '../components/PageTransition';

const MODEL_META = [
  { id: 'gcn', name: 'GCN', badge: 'badge-gcn', icon: '🔵' },
  { id: 'gat', name: 'GAT', badge: 'badge-gat', icon: '🟣' },
  { id: 'graphsage', name: 'GraphSAGE', badge: 'badge-graphsage', icon: '🟢' },
  { id: 'mlp', name: 'MLP', badge: 'badge-mlp', icon: '🟡' },
];

export const PaperExplorer: React.FC = () => {
  const [nodeId, setNodeId] = useState('');
  const [nodeDetails, setNodeDetails] = useState<NodeDetails | null>(null);
  const [comparisons, setComparisons] = useState<PredictionResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    const id = parseInt(nodeId.trim());
    if (isNaN(id)) {
      setError('Please enter a valid node ID (0-2707)');
      return;
    }

    setLoading(true);
    setError(null);
    setNodeDetails(null);
    setComparisons(null);

    try {
      // Fetch node details
      const detailsRes = await gnnApi.getNodeInfo(id);
      setNodeDetails(detailsRes.data);

      // Compare all models
      const results = await gnnApi.compareModels(id);
      setComparisons(results);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Node not found or prediction failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition className="explorer-page">
      <div className="page-header">
        <h1>Paper Explorer</h1>
        <p>Look up any paper in the Cora dataset and compare predictions across all models</p>
      </div>

      {/* Search */}
      <div className="search-section glass-card">
        <h3>
          <span className="section-icon">🔍</span>
          Search Paper
        </h3>
        <div className="search-bar">
          <input
            type="text"
            value={nodeId}
            onChange={(e) => setNodeId(e.target.value)}
            placeholder="Enter paper node ID (e.g., 42)"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            className="btn-primary"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></span>
                Searching...
              </>
            ) : (
              '🔎 Search'
            )}
          </button>
        </div>
        {error && (
          <p style={{ marginTop: '0.75rem', color: 'var(--danger)', fontSize: '0.85rem' }}>
            ❌ {error}
          </p>
        )}
      </div>

      {/* Node Details */}
      {nodeDetails && (
        <div className="node-details" style={{ marginTop: '1.5rem' }}>
          <div className="glass-card">
            <h3>
              <span className="section-icon">📄</span>
              Paper #{nodeDetails.node_id} — Details
            </h3>
            <div className="node-details-grid">
              <div className="detail-item">
                <div className="detail-label">Node ID</div>
                <div className="detail-value">{nodeDetails.node_id}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">True Label</div>
                <div className="detail-value">{nodeDetails.true_class}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Degree (Neighbors)</div>
                <div className="detail-value">{nodeDetails.degree}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Active Features</div>
                <div className="detail-value">
                  {nodeDetails.feature_summary.active_features} / {nodeDetails.feature_summary.total_features}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Model Comparison */}
      {comparisons && (
        <div className="model-comparison" style={{ marginTop: '1.5rem' }}>
          <div className="glass-card">
            <h3>
              <span className="section-icon">⚔️</span>
              Model Comparison
            </h3>
            <div className="comparison-grid">
              {comparisons.map((result, idx) => {
                const pred = result.predictions[0];
                const meta = MODEL_META[idx];
                const confLevel = pred.confidence > 0.8 ? 'high' : pred.confidence > 0.6 ? 'medium' : 'low';

                // SVG ring
                const radius = 22;
                const circumference = 2 * Math.PI * radius;
                const offset = circumference - (pred.confidence * circumference);

                return (
                  <div key={meta.id} className="comparison-card">
                    <div className="model-name">
                      <span>{meta.icon}</span>
                      <span className={`model-badge ${meta.badge}`}>{meta.name}</span>
                      <span style={{
                        marginLeft: 'auto',
                        fontSize: '0.7rem',
                        color: 'var(--text-muted)',
                        fontFamily: "'JetBrains Mono', monospace"
                      }}>
                        {result.inference_time_ms}ms
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div className="confidence-ring">
                        <svg viewBox="0 0 54 54">
                          <circle className="ring-bg" cx="27" cy="27" r={radius} />
                          <circle
                            className={`ring-fill ${confLevel}`}
                            cx="27" cy="27" r={radius}
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                          />
                        </svg>
                        <span className={`ring-label ${confLevel}`}>
                          {(pred.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div>
                        <div style={{
                          fontSize: '0.7rem',
                          color: 'var(--text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          fontWeight: 600,
                          marginBottom: '0.2rem'
                        }}>
                          Predicted
                        </div>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: 'var(--text-accent)'
                        }}>
                          {pred.class_name}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="loading-state" style={{ marginTop: '2rem' }}>
          <div className="spinner spinner-lg"></div>
          <p>Running all 4 models...</p>
        </div>
      )}
    </PageTransition>
  );
};
